/**
 * Gate hook for the step-2 redesign test (`step2_redesign_v1`). Resolves
 * which step-2 experience to render and, for ASSIGNED visitors only, fires
 * the standard test side effects (checkout `ab` attributes via abTestVar,
 * the server-side impression, and the GA impression event).
 *
 * Returns null until resolved (render nothing to avoid flashing the wrong
 * arm), then 'A' (control) or 'B' (redesign). There is deliberately NO
 * country gate — GBR participates; countryCode is only forwarded into
 * recordImpression so the readout can slice by country.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { abTestVar } from 'store/utils';
import {
  ABVariant,
  STEP2_REDESIGN_ACTIVE,
  STEP2_REDESIGN_TEST,
  getStep2VariantFromUrl,
  getVariant,
  recordImpression,
  setAnonId,
  setVariant,
  trackABEvent,
} from './abTest';
import { removeStep2RedesignGuard } from './step2RedesignGuard';

// useLayoutEffect on the client so the variant resolves before paint (no
// flash of the wrong arm); useEffect on the server, where useLayoutEffect
// triggers the Next.js SSR warning. Deliberately NOT a lazy useState
// initializer: that would read cookies/URL during the hydration render and
// risk a server/client hydration mismatch.
const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

/**
 * Pure, side-effect-free resolution of the variant to RENDER, safe to run
 * inside the first client render (lazy useState initializer). Returns null on
 * the server, so SSG markup is always control.
 *
 * Why the render decision is synchronous: if the first client render showed
 * control and a later effect swapped to B, the control tree would MOUNT for
 * every Variant B visitor and fire its mount analytics (GTM `step_2`, Elevar
 * user-data, the Shopify pageview) before unmounting — once on A, twice on B:
 * an arm asymmetry (observed in QA as the NewFlowFAQ unmounted-setState
 * warning). Resolving synchronously means control NEVER mounts for B. The
 * server/client hydration difference this creates is confined to the page's
 * single variant conditional and is visually masked by the pre-hydration
 * guard; unassigned and Variant A visitors match the server HTML exactly.
 * All WRITES (cookie, abTestVar, impression, GA) stay in the layout effect.
 */
export function resolveStep2RedesignRenderVariant(): ABVariant | null {
  if (typeof window === 'undefined') return null;
  if (!STEP2_REDESIGN_ACTIVE) return 'A';
  const fromUrl = getStep2VariantFromUrl();
  if (fromUrl) return fromUrl.variant;
  return getVariant(STEP2_REDESIGN_TEST.name) || 'A';
}

export function useStep2RedesignVariant(countryCode: string): ABVariant | null {
  const [variant, setVariantState] = useState<ABVariant | null>(
    resolveStep2RedesignRenderVariant
  );
  // Once-per-mount guard: React dev mode double-fires effects, which would
  // double the impression/GA side effects (mirrors the ref guard the retired
  // home-redesign adoption effect used in NewStepTwo).
  const hasResolved = useRef(false);

  useIsomorphicLayoutEffect(() => {
    if (hasResolved.current) return;
    hasResolved.current = true;

    // (a) Kill switch: test off -> everyone sees control and NOBODY is
    // assigned. No cookie writes, no abTestVar, no impression, no GA event.
    if (!STEP2_REDESIGN_ACTIVE) {
      removeStep2RedesignGuard();
      setVariantState('A');
      return;
    }

    // (b) Arm forwarded from the Rollouts homepage via `hv` (+ optional
    // `vid`): honor it, persist it in the `tcc_ab_<test>` cookie so return
    // visits stay in the same arm, and adopt the homepage-minted id whenever
    // it survived the redirect (local anon id otherwise suffices as join key).
    const fromUrl = getStep2VariantFromUrl();
    let resolved: ABVariant | null = null;
    if (fromUrl) {
      resolved = fromUrl.variant;
      setVariant(STEP2_REDESIGN_TEST.name, fromUrl.variant);
      if (fromUrl.vid) setAnonId(fromUrl.vid);
    } else {
      // (c) No URL params: sticky cookie from a previously assigned visit
      // keeps the returning visitor in their arm.
      resolved = getVariant(STEP2_REDESIGN_TEST.name);
    }

    // (d) Direct traffic (no `hv`, no cookie) is NEVER assigned: control
    // experience, no side effects — excluded symmetrically from both arms.
    if (!resolved) {
      removeStep2RedesignGuard();
      setVariantState('A');
      return;
    }

    // Assigned visitor (paths b/c only): fire the standard side effects once
    // per mount. abTestVar feeds the existing checkout `ab` attributes
    // injection (control Categories reads it); recordImpression is the DB
    // funnel denominator; trackABEvent is the GA-side impression.
    abTestVar({ experiment: STEP2_REDESIGN_TEST.name, variant: resolved });
    recordImpression(STEP2_REDESIGN_TEST.name, resolved, countryCode);
    trackABEvent(
      STEP2_REDESIGN_TEST.events.impression,
      STEP2_REDESIGN_TEST.name,
      resolved
    );
    // Variant resolved: drop the pre-hydration guard. For 'A' this un-hides
    // control before the next paint; for 'B' the control root unmounts anyway.
    removeStep2RedesignGuard();
    setVariantState(resolved);
  }, []);

  return variant;
}

export default useStep2RedesignVariant;
